import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { FileText, X, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `₹${Number(n || 0).toFixed(2)}`;

export default function CreditNotes() {
  const { token, user } = useAuth();
  const [notes, setNotes] = useState([]),
    [selected, setSelected] = useState(null),
    [search, setSearch] = useState(""),
    [statusFilter, setStatusFilter] = useState("all"),
    [editing, setEditing] = useState(false);
  const load = () =>
    axios
      .get(`${API_URL}/credit-notes`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setNotes(r.data || []))
      .catch(() => {});
  useEffect(() => {
    load();
  }, [token]);
  const visibleNotes = useMemo(
    () =>
      notes.filter((n) => {
        const q = search.trim().toLowerCase();
        return (
          (!q ||
            [
              n.credit_note_number,
              n.customer_name,
              n.customer_phone,
              n.original_invoice_number,
            ].some((v) =>
              String(v || "")
                .toLowerCase()
                .includes(q),
            )) &&
          (statusFilter === "all" || n.status === statusFilter)
        );
      }),
    [notes, search, statusFilter],
  );
  
  const canManage =
    user?.role === "admin" && Number(selected?.credit_used || 0) === 0;

  const saveEdit = async () => {
    try {
      const items = (selected.returned_items || []).map((i) => ({
        item_index: i.item_index,
        quantity: Number(i.quantity),
      }));
      const r = await axios.put(
        `${API_URL}/credit-notes/${selected.id}`,
        { items },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSelected(r.data);
      setEditing(false);
      load();
      toast.success("Credit note updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update credit note");
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this unused credit note?")) return;
    try {
      await axios.delete(`${API_URL}/credit-notes/${selected.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelected(null);
      load();
      toast.success("Credit note deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete credit note");
    }
  };
  return (
    <div className="p-6 h-full overflow-auto">
      <div className="flex items-center gap-3 mb-6">
        <FileText className="text-amber-500" />
        <div>
          <h1 className="text-2xl font-black text-stone-800">Credit Notes</h1>
          <p className="text-sm text-stone-500">
            Returns and available customer store credit
          </p>
        </div>
      </div>
      <div className="flex gap-2 mb-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search credit note, customer, phone, invoice"
          className="h-9 flex-1 rounded-xl border border-stone-200 bg-white px-3 text-xs outline-none"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-xl border border-stone-200 bg-white px-3 text-xs"
        >
          <option value="all">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="PARTIALLY_USED">Partially Used</option>
          <option value="REDEEMED">Redeemed</option>
        </select>
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 overflow-auto">
        <table className="w-full text-left">
          <thead className="bg-stone-50 text-xs uppercase text-stone-500">
            <tr>
              {[
                "Credit Note",
                "Customer",
                "Original Order",
                "Issued",
                "Used",
                "Remaining",
                "Status",
              ].map((h) => (
                <th key={h} className="p-4">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleNotes.map((n) => (
              <tr
                key={n.id}
                onClick={() => setSelected(n)}
                className="border-t border-stone-100 text-sm cursor-pointer hover:bg-amber-50"
              >
                <td className="p-4 font-bold">{n.credit_note_number}</td>
                <td className="p-4">
                  {n.customer_name || "—"}
                  <br />
                  <span className="text-xs text-stone-400">
                    {n.customer_phone}
                  </span>
                </td>
                <td className="p-4">{n.original_invoice_number}</td>
                <td className="p-4">{money(n.credit_issued)}</td>
                <td className="p-4">{money(n.credit_used)}</td>
                <td className="p-4 font-bold text-emerald-600">
                  {money(n.remaining_credit)}
                </td>
                <td className="p-4">
                  <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-bold">
                    {n.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!visibleNotes.length && (
          <p className="p-8 text-center text-stone-400">
            No matching credit notes.
          </p>
        )}
      </div>
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between mb-5">
              <div>
                <h2 className="text-xl font-black">
                  {selected.credit_note_number}
                </h2>
                <p className="text-sm text-stone-500">
                  {selected.customer_name} · {selected.customer_phone}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  disabled={!canManage}
                  onClick={() => setEditing(true)}
                  className="disabled:opacity-30"
                >
                  <Pencil size={17} />
                </button>
                <button
                  disabled={!canManage}
                  onClick={remove}
                  className="text-rose-600 disabled:opacity-30"
                >
                  <Trash2 size={17} />
                </button>
                <button onClick={() => setSelected(null)}>
                  <X />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm mb-5">
              <p>
                Original Order
                <br />
                <b>{selected.original_invoice_number}</b>
              </p>
              <p>
                Purchase Date
                <br />
                <b>
                  {selected.original_purchase_date
                    ? new Date(
                        selected.original_purchase_date,
                      ).toLocaleDateString("en-IN")
                    : "—"}
                </b>
              </p>
              <p>
                Credit Issued
                <br />
                <b>{money(selected.credit_issued)}</b>
              </p>
              <p>
                Credit Used
                <br />
                <b>{money(selected.credit_used)}</b>
              </p>
              <p>
                Remaining
                <br />
                <b className="text-emerald-600">
                  {money(selected.remaining_credit)}
                </b>
              </p>
              <p>
                Status
                <br />
                <b>{selected.status}</b>
              </p>
            </div>
            <h3 className="font-bold mb-2">Returned Items</h3>
            {(selected.returned_items || []).map((item, i) => (
              <div key={i} className="border rounded-lg p-3 mb-2 text-sm">
                <b>{item.product_name}</b>
                <span className="float-right">{money(item.total)}</span>
                {editing && (
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) =>
                      setSelected((s) => ({
                        ...s,
                        returned_items: s.returned_items.map((x, j) =>
                          j === i
                            ? {
                                ...x,
                                quantity: Number(e.target.value) || 1,
                                total:
                                  (Number(e.target.value) || 1) *
                                  Number(x.price || 0),
                              }
                            : x,
                        ),
                      }))
                    }
                    className="w-20 ml-3 border rounded p-1"
                  />
                )}
                <p className="text-stone-500">
                  Qty: {item.quantity} · Original price: {money(item.price)}
                </p>
              </div>
            ))}
            {editing && (
              <button
                onClick={saveEdit}
                className="w-full rounded-xl bg-amber-500 py-2 font-bold mb-4"
              >
                Save Changes
              </button>
            )}
            <h3 className="font-bold mt-5 mb-2">Usage History</h3>
            {(selected.usage_history || []).length ? (
              selected.usage_history.map((u, i) => (
                <div key={i} className="text-sm border-b py-2">
                  {money(u.amount)} · Order:{" "}
                  {u.order_number || u.order_id || "Pending"}
                </div>
              ))
            ) : (
              <p className="text-sm text-stone-400">No usage yet.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
